import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@/components/Card/Card';
import { BundleDetails } from '@/types/bundles.types';
import { renderOccasions } from '@/utils/formatting/formatBundleProperties';
import { FreezeFormValues } from '../CreateFreeze/CreateFreeze.types';
import styles from './Steps.module.css';
import { Container, Grid } from '@mui/material';

const formatDate = (input = '') => input.length >= 10 && input.slice(0, 10);

export const FreezeStepThree = ({
  bundle,
  editing,
  initialFormData,
}: {
  editing?: boolean;
  bundle: BundleDetails | undefined;
  initialFormData: FreezeFormValues;
}) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState<FreezeFormValues>(initialFormData);

  const occasion = renderOccasions(t, bundle?.occasions);

  return (
    <Container fixed>
      <Grid container direction="column" justifyContent="center" alignItems="center" spacing={4}>
        <Grid item mt={6}>
          <Card className={styles.fixedWith}>
            <h3 className={styles.cardHeader}>{t('createFreezeModal.stepThree.bundle.header')}</h3>
            <div className={styles.divider} />
            <div className={styles.row}>
              <div className={styles.column}>
                <p>{t('createFreezeModal.stepThree.bundle.displayName')}: </p>
                <p>{t('createFreezeModal.stepThree.bundle.bundleCode')}: </p>
                <p>{t('createFreezeModal.stepThree.bundle.bundleDescription')}: </p>
                <p>{t('createFreezeModal.stepThree.bundle.occasion')}: </p>
              </div>
              <div className={styles.columnDescriptions}>
                <p>{bundle?.name}</p>
                <p>{bundle?.code}</p>
                <p>{bundle?.internalName}</p>
                <p>{occasion}</p>
              </div>
            </div>
          </Card>
        </Grid>
        <Grid item>
          <Card className={styles.fixedWith}>
            <h3 className={styles.cardHeader}>{t('createFreezeModal.stepThree.nationalProgram.header')}</h3>
            <div className={styles.divider} />
            <div className={styles.widgetBody}>
              <Grid container direction="row" spacing={3}>
                <Grid item xs={6}>
                  <div className={styles.row}>
                    <div className={styles.column}>
                      <p>{t('createFreezeModal.stepThree.nationalProgram.freezeType')}: </p>
                      <p>{t('createFreezeModal.stepThree.nationalProgram.freezeName')}: </p>
                      <p>{t('createFreezeModal.stepThree.nationalProgram.optInStartDate')}: </p>
                      <p>{t('createFreezeModal.stepThree.nationalProgram.programStartDate')}: </p>
                      <p>{t('createFreezeModal.stepThree.nationalProgram.programEndDate')}: </p>
                      <p>{t('createFreezeModal.stepThree.nationalProgram.optOutSettingsHeader')}: </p>
                      <div className={styles.space} />
                    </div>
                    <div className={styles.columnDescriptions}>
                      <p>
                        {formValues?.freezeType === 'nationalProgram'
                          ? t('createFreezeModal.stepThree.nationalProgram.freezeTypeNationalProgram')
                          : t('createFreezeModal.stepThree.nationalProgram.freezeTypeMaintenance')}
                      </p>
                      <p>{formValues.freezeName || '-'}</p>
                      <p>{formatDate(formValues.optInStartDate) || '-'}</p>
                      <p>{formatDate(formValues.freezeStartDate) || '-'}</p>
                      <p>{formatDate(formValues.freezeEndDate) || '-'}</p>
                      <p>{formValues.optOutHidden ? t('common.hide') : t('common.show')}</p>
                      <div className={styles.space} />
                    </div>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className={styles.row}>
                    <div className={styles.column}>
                      {formValues.defaultPrice && (
                        <p>{t('createFreezeModal.stepThree.nationalProgram.nationalPricePointLabel')}: </p>
                      )}
                      {formValues.nationalPricePointTwo && (
                        <p>{t('createFreezeModal.stepThree.nationalProgram.pricePoint2Label')}: </p>
                      )}
                      {formValues.nationalPricePointThree && (
                        <p>{t('createFreezeModal.stepThree.nationalProgram.pricePoint3Label')}: </p>
                      )}
                      {formValues.nationalPricePointFour && (
                        <p>{t('createFreezeModal.stepThree.nationalProgram.pricePoint4Label')}: </p>
                      )}
                      <p>{t('createFreezeModal.stepThree.nationalProgram.redeemableOnline')}: </p>
                      <p>{t('createFreezeModal.stepThree.nationalProgram.visibleOnline')}: </p>

                      <div className={styles.space} />
                    </div>
                    <div className={styles.columnDescriptions}>
                      {formValues.defaultPrice && <p>{`$${Number(formValues.defaultPrice).toFixed(2)}`}</p>}
                      {formValues.nationalPricePointTwo && (
                        <p>{`$${Number(formValues.nationalPricePointTwo).toFixed(2)}`}</p>
                      )}
                      {formValues.nationalPricePointThree && (
                        <p>{`$${Number(formValues.nationalPricePointThree).toFixed(2)}`}</p>
                      )}
                      {formValues.nationalPricePointFour && (
                        <p>{`$${Number(formValues.nationalPricePointFour).toFixed(2)}`}</p>
                      )}
                      <p>{formValues.redeemableOnline ? t('common.yes') : t('common.no')}</p>
                      <p>{formValues.visibleOnline ? t('common.yes') : t('common.no')}</p>

                      <div className={styles.space} />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};
